import { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import { SessionContext } from "../utility/useSession";
import { SESSION_LAST_ACTIVITY_KEY, SESSION_LOGOUT_SIGNAL_KEY } from "@/utility/sessionStorageKeys";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { throttle } from "lodash";

export interface SessionTimeoutConfig {
    timeoutMinutes: number;
    warningMinutes: number;
}

export interface UseSessionTimeoutResult {
    timeRemaining: number;
    isWarning: boolean;
    isExpired: boolean;
    isActive: boolean;
    isIdle: boolean;
    extend: () => void;
    logout: () => void;
    isEnabled: boolean;
    isTestingEnabled: boolean;
    config: SessionTimeoutConfig;
    updateConfig: (newConfig: Partial<SessionTimeoutConfig>) => void;
}

const getLastActivityTime = () => {
    const stored = localStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
    if (!stored) {
        return Date.now();
    }

    const parsed = Number(stored);
    return Number.isNaN(parsed) ? Date.now() : parsed;
};

const isValidConfig = (config: SessionTimeoutConfig) => {
    if (config.timeoutMinutes <= 0 || config.warningMinutes <= 0) {
        console.error('Session timeout configuration invalid: times must be positive');
        return false;
    }
    if (config.warningMinutes >= config.timeoutMinutes) {
        console.error('Session timeout configuration invalid: warning time must be less than timeout');
        return false;
    }
    return true;
};


export function useSessionTimeout(
    externalConfig?: SessionTimeoutConfig,
    externalUpdateConfig?: (newConfig: Partial<SessionTimeoutConfig>) => void
): UseSessionTimeoutResult {
    const { current, logout: sessionLogout } = useContext(SessionContext);

    const [state, setState] = useState({
        timeRemaining: 0,
        isWarning: false,
        isExpired: false,
        isIdle: false,
        lastActivity: Date.now(),
        config: externalConfig || {
            timeoutMinutes: 60,
            warningMinutes: 2
        }
    });

    const isEnabled = useReleaseManagement(ReleaseFlag.releaseSessionInactivityTimeoutDuration);
    const isTestingEnabled = useReleaseManagement(ReleaseFlag.releaseQATools);
    const isAuthenticated = !!current?.credential && !!current?.site;
    const isActive = isAuthenticated && !state.isExpired && isEnabled;

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isActiveRef = useRef<boolean>(true);

    // throttled localStorage update (1 second throttle with immediate execution)
    // or lese we risk to never update the last activity time
    // if user never goes inactive
    const throttledUpdateActivity = useMemo(
        () => throttle((timestamp: number) => {
            localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, timestamp.toString());
        }, 1000, { leading: true, trailing: false }),
        []
    );

    const updateConfig = useCallback((newConfig: Partial<SessionTimeoutConfig>) => {
        const mergedConfig = { ...(externalConfig || state.config), ...newConfig };

        if (!isValidConfig(mergedConfig)) {
            return;
        }

        if (externalUpdateConfig) {
            externalUpdateConfig(newConfig);
        } else {
            setState(prev => ({
                ...prev,
                config: mergedConfig
            }));
        }
    }, [externalUpdateConfig, externalConfig, state.config]);

    const resetActivity = useCallback(() => {
        if (!isAuthenticated || !isEnabled) return;

        const now = Date.now();
        setState(prev => {
            // Don't extend session if we're in warning state 
            if (prev.isWarning) {
                return prev;
            }

            const newState = {
                ...prev,
                lastActivity: now,
                isIdle: false
            };

            // update localStorage if we're extending the session
            throttledUpdateActivity(now);

            return newState;
        });
    }, [isAuthenticated, isEnabled, throttledUpdateActivity]);

    const throttledResetActivity = useMemo(
        () => throttle(resetActivity, 100),
        [resetActivity]
    );

    const extend = useCallback(() => {
        // Explicit extension should always work, even during warning state
        if (!isAuthenticated || !isEnabled) {
            return;
        }

        const now = Date.now();
        setState(prev => ({
            ...prev,
            lastActivity: now,
            isIdle: false,
            isWarning: false
        }));

        throttledUpdateActivity(now);
    }, [isAuthenticated, isEnabled, throttledUpdateActivity]);

    const logout = useCallback(() => {
        localStorage.setItem(SESSION_LOGOUT_SIGNAL_KEY, Date.now().toString());
        sessionLogout();
    }, [sessionLogout]);

    useEffect(() => {
        isActiveRef.current = true;

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (!isAuthenticated || !isEnabled) {
            setState(prev => ({
                ...prev,
                timeRemaining: 0,
                isWarning: false,
                isExpired: false,
                isIdle: false
            }));
            return;
        }

        const updateTimer = () => {
            if (!isActiveRef.current) return;

            const now = Date.now();
            const lastActivityTime = getLastActivityTime();

            setState(prev => {
                const elapsed = now - lastActivityTime;
                const currentConfig = externalConfig || prev.config;
                const timeoutMs = currentConfig.timeoutMinutes * 60 * 1000;
                const remaining = Math.max(0, timeoutMs - elapsed);
                const warningThreshold = currentConfig.warningMinutes * 60 * 1000;

                // we need the 1 second buffer to account for rendering delay
                // not really necessary, but it's better UX
                const isWarningCalc = remaining <= (warningThreshold + 1000) && remaining > 0;
                const isExpiredCalc = remaining === 0;

                // update localStorage on expiration once
                if (isExpiredCalc && !prev.isExpired) {
                    localStorage.setItem(SESSION_LOGOUT_SIGNAL_KEY, Date.now().toString());
                }

                return {
                    ...prev,
                    timeRemaining: remaining,
                    isWarning: isWarningCalc,
                    isIdle: elapsed > 10000,
                    isExpired: isExpiredCalc,
                    lastActivity: lastActivityTime
                };
            });

            if (isActiveRef.current) {
                timerRef.current = setTimeout(updateTimer, 1000);
            }
        };

        // start the timer
        updateTimer();

        return () => {
            isActiveRef.current = false;

            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            throttledUpdateActivity.cancel();
        };
    }, [
        externalConfig ? externalConfig.timeoutMinutes : state.config.timeoutMinutes,
        externalConfig ? externalConfig.warningMinutes : state.config.warningMinutes,
        isAuthenticated,
        isEnabled,
        throttledUpdateActivity
    ]);

    // Listen for logout signals from other tabs
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === SESSION_LOGOUT_SIGNAL_KEY && e.newValue) {
                // Another tab triggered logout
                sessionLogout();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [sessionLogout]);

    useEffect(() => {
        if (!isAuthenticated || !isEnabled || state.isWarning) {
            return;
        }

        const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];

        events.forEach(event => {
            window.addEventListener(event, throttledResetActivity, { passive: true });
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, throttledResetActivity);
            });

            throttledResetActivity.cancel();
        };
    }, [isAuthenticated, isEnabled, throttledResetActivity]);

    useEffect(() => {
        return () => {
            throttledUpdateActivity.cancel();
            throttledResetActivity.cancel();
        };
    }, [throttledUpdateActivity, throttledResetActivity]);

    return {
        timeRemaining: state.timeRemaining,
        isWarning: state.isWarning,
        isExpired: state.isExpired,
        isActive,
        isIdle: state.isIdle,
        extend,
        logout,
        isEnabled,
        isTestingEnabled,
        config: externalConfig || state.config,
        updateConfig
    };
}
